import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ApiService } from 'src/app/core/api.service';
import { Base64Service } from 'src/app/core/base64.service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { FileItemService } from 'src/app/file/file-item.service';
import { LinkFileListService } from 'src/app/link-consume/services/link-file-list.service';
import { BlendEvent, ErrCode, LinkListItem } from 'src/app/shared/models';
import { BlendService } from 'src/app/shared/services/blend.service';
import { renameLinkOptions } from 'src/app/util/blend-property-names';
import * as fromRoot from 'src/app/reducers';
import { IFile } from 'src/sync';
import * as LinkListActions from 'src/app/actions/link-list.actions';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { Subscription } from 'rxjs';
import { mapFormToApiParams } from '../link-permissions-form/link-permissions-form.component';
import { ChargebeeService } from 'src/app/shared/services/chargebee.service';
import { IDialogLinkManageFormData } from "../types";

@Component({
  selector: 'app-link-settings',
  templateUrl: './link-settings.component.html',
  styleUrls: ['./link-settings.component.scss']
})
export class LinkSettingsComponent implements OnInit, OnDestroy {
  
  @Input() link: any;
  @Input() item: LinkListItem | IFile;
  @Input() fileType: string;
  @Input() fileName: string;
  @Input() showSettingsLinkDialog: boolean;
  @Input() openState: number;
  @Input() savedFormData: IDialogLinkManageFormData;
  
  @Output() state = new EventEmitter<number>();
  @Output() showSettingsLinkDialogChange = new EventEmitter<boolean>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter<boolean>();
  
  
  public expirationDate: { year: number; month: number; day: number };
  public expirationTime: { hour: number; minute: number; second: number };
  
  public isPro = false;
  public errcode: ErrCode;
  public hasComment = false;
  public hasBetaAccess = false;
  public isAppLink = false;
  
  public linkForm: FormGroup;
  public previewFieldValue: string;
  
  public isMSOfficeFile = false;
  public isButtonLoading: boolean = false;
  public commentsEnabled: boolean = false;
  public limitEnabled: boolean = false;
  public enhancedPrivacyEnabled: boolean = false;
  public newCommentsNotification: boolean = false;
  public newDownloadsNotification: boolean = false;
  public allowCommentsAuthenticatedUser: boolean = false;
  public allowCommentsOptions: any
  public downloadLimit: string = '';
  public enhancedPrivacy: boolean = false;
  public errorMessage: string = '';
  private fieldSubscription: Subscription;
  public labelName: string = '';
  
  constructor(
    private api: ApiService,
    private base64: Base64Service,
    private fb: FormBuilder,
    private log: LoggerService,
    private store: Store<fromRoot.State>,
    private userService: UserService,
    private snackbarService: SnackbarService,
    private router: Router,
    private crypt: SyncCryptService,
    private linkFileList: LinkFileListService,
    private blendService: BlendService,
    private fileItemService: FileItemService,
    private chargebeeService: ChargebeeService
  ) {
  }
  
  ngOnInit() {
    const apiParams = mapFormToApiParams(
      this.savedFormData.permission,
      this.savedFormData.upload,
      this.link.type
    );
    this.link.previewonly = apiParams.previewonly;
    this.link.file_edit = apiParams.file_edit;
    this.link.upload = apiParams.upload;
    
    this.newCommentsNotification = this.link.comment_notification !== 0;
    this.newDownloadsNotification = this.link.email_notification !== 0;
    this.allowCommentsAuthenticatedUser = this.link.allow_comment !== 1;
    
    this.downloadLimit = this.link.download_limit || '';
    this.limitEnabled = !!this.link.download_limit;
    
    this.enhancedPrivacy = this.link.compat !== 1;
    this.commentsEnabled = this.link.allow_comment !== 0;
    this.labelName = this.base64.decode(this.link.label);
    this.initCommentOptions();
    
    this.isMSOfficeFile = this.fileItemService.isMSOffice(this.item as IFile, 'edit');
    
    this.isPro = this.userService.get('is_pro');
    this.hasComment = this.userService.get('has_comment');
    this.hasBetaAccess = this.userService.get('has_beta_access');
    this.isAppLink = this.link.baseUrl.includes('/al');
    
    const hasExpirationDate = this.savedFormData?.exp_date || this.link.exp_servtime
    if (hasExpirationDate) {
      const expirationDate = new Date(this.savedFormData?.exp_date || this.link.exp_servtime);
      this.expirationDate = {
        year: expirationDate.getFullYear(),
        month: expirationDate.getMonth() + 1,
        day: expirationDate.getDate(),
      };
      let [hours, minutes] = this.savedFormData?.exp_time.split(":").map(Number);
      this.expirationTime = {
        hour: hours || expirationDate.getHours(),
        minute: minutes || expirationDate.getMinutes(),
        second: 0,
      };
    }
    //if file_edit is enabled, file is not shared and file is MSOffice editable, enable the third radio button for edit file in file permissions
    //else check previewonly link setting and enable preview/download radio button
    if (this.link.file_edit === 1 && (!this.item.is_shared && (this.link.type != 'file' || this.isMSOfficeFile))) {
      this.previewFieldValue = 'edit';
    } else {
      this.previewFieldValue = this.link.previewonly === 0 ? 'download' : 'preview';
    }
    
    this.linkForm = this.fb.group({
      passwordprotect: [this.link.passwordprotect],
      label: [this.base64.decode(this.link.label)],
      key: [this.link.key],
      upload: [this.link.upload.toString() || '0', Validators.required],
      previewonly: [
        this.previewFieldValue,
        Validators.required,
      ],
      download_limit: [this.link.download_limit],
      email_notification: [
        this.link.email_notification.toString() || '0',
        Validators.required,
      ],
      compat: [this.link.compat.toString() || '0', Validators.required],
      exp_date: [this.expirationDate],
      exp_time: [this.expirationTime],
      allow_comment: [
        this.link.allow_comment.toString() || '1',
        Validators.required,
      ],
      comment_notification: [
        this.link.comment_notification.toString() || '1',
        Validators.required,
      ],
    });
    
    this.fieldSubscription = this.linkForm.controls.upload.valueChanges.subscribe((uploadFieldValue) => {
      // file permission radio buttons are disbled according to the upload radio values
      // reset previewonly field value when all file permission radio buttons are disabled
      // or when the edit file radio button is disabled
      if (this.link.type === 'dir') {
        if (uploadFieldValue === '2' || (uploadFieldValue === '0' && this.linkForm.value.previewonly === 'edit')) {
          this.linkForm.controls.previewonly.setValue(this.link.previewonly === 0 ? 'download' : 'preview');
        }
      }
    });
    
    this.showSettingsLinkDialog = true;
    if (this.link.is_app_link != 0) this.enhancedPrivacyEnabled = true;
  }
  
  ngOnDestroy(): void {
    this.fieldSubscription?.unsubscribe();
  }
  
  initCommentOptions = () => {
    this.allowCommentsOptions = [
      {
        label: "Authenticated users only",
        value: "1",
        disabled: false,
        isChecked: this.commentsEnabled && this.link.allow_comment === 1,
      },
      {
        label: "Anyone with the link",
        value: "2",
        disabled: false,
        isChecked: this.commentsEnabled && this.link.allow_comment === 2,
      },
    ]
    
    // If `commentsEnabled` is true but no option is selected, set the first option to true by default
    if (this.commentsEnabled && this.link.allow_comment === 0) {
      this.link.allow_comment = 1;
      this.initCommentOptions();
    }
  }
  
  newCommentsNotificationChange = (checkboxChange: MatCheckboxChange) => {
    this.newCommentsNotification = checkboxChange.checked;
  }
  
  newDownloadsNotificationChange = (checkboxChange: MatCheckboxChange) => {
    this.newDownloadsNotification = checkboxChange.checked;
  }
  
  allowCommentsChange = (event: any) => {
    this.allowCommentsAuthenticatedUser = event.value
  }
  
  downloadLimitChange = (downloadLimitChange: string) => {
    const parsedLimit = parseInt(downloadLimitChange, 10);
    if (parsedLimit < 0) {
      this.downloadLimit = '';
      this.errorMessage = 'Enter a valid download limit.'
    } else {
      this.errorMessage = '';
      this.downloadLimit = downloadLimitChange;
    }
  }
  
  public onSubmit = async () => {
    try {
      this.errcode = null;
      
      const expirationDate = this.linkForm.get('exp_date').value;
      let expirationTime = this.linkForm.get('exp_time').value;
      let expServtime;
      if (expirationDate) {
        const dateTime = new Date(
          expirationDate.year,
          expirationDate.month - 1,
          expirationDate.day,
          (expirationTime || new Date().getHours()).hour,
          (expirationTime || new Date().getMinutes()).minute,
          0
        );
        expServtime = dateTime.getTime();
      }
      
      
      const allowComment = !this.commentsEnabled ? 0 : this.allowCommentsAuthenticatedUser;
      
      const linkSetOptions = {
        sync_id: this.link.sync_id,
        share_id: this.link.share_id,
        publink_id: this.link.cachekey,
        label: this.base64.encode(this.labelName),
        exp_servtime: parseInt(expServtime, 10),
        download_limit: parseInt(this.downloadLimit, 10),
        email_notification: this.newDownloadsNotification,
        upload: parseInt(this.linkForm.get('upload').value, 10),
        
        // if preview only option is selected from file permissions, set previewonly in link options
        previewonly: this.linkForm.value.previewonly === 'preview' ? 1 : 0,
        
        // if edit option is selected from file permissions, set file_edit in link options
        // this will set the lnkFILEEDIT in LinkFeatures
        file_edit: this.linkForm.value.previewonly === 'edit' ? 1 : 0,
        compat: this.enhancedPrivacy ? 0 : 1,
        allow_comment: allowComment,
        comment_notification: this.newCommentsNotification
      };
      
      // if the link is to a file and edit permission is enabled, enable the upload permission as well
      if (this.link.type === 'file') {
        linkSetOptions.upload = this.linkForm.value.previewonly === 'edit' ? 1 : 0;
      }
      
      await this.api.execute('linksetoptions', linkSetOptions);
      this.blendService.track(BlendEvent.SAVE_LINK_SETTINGS, renameLinkOptions({
        ...linkSetOptions,
        type: this.link.type,
        file_extension: this.item.file_extension
      }));
      
      this.store.dispatch(new LinkListActions.LinkListRefreshAction());
      this.snackbarService.showMessage("success", "Link settings successfully saved.");
    } catch (error) {
      this.closeSettingsLinkDialog();
      this.errcode = ErrCode.fromException(error);
      this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
    }
  }
  
  toggleComments = (event: MatSlideToggleChange) => {
    this.commentsEnabled = event.checked;
    this.initCommentOptions();
  }
  
  toggleDownloadLimit = (event: MatSlideToggleChange) => {
    this.limitEnabled = event.checked;
    if (!event.checked) {
      this.downloadLimit = null;
    } else {
      this.downloadLimit = '1';
    }
  }
  
  togglePrivacy = (event: MatSlideToggleChange) => {
    this.enhancedPrivacy = event.checked;
  }
  
  onBack = () => {
    this.showSettingsLinkDialog = false;
    setTimeout(() => {
      this.showSettingsLinkDialogChange.next(false);
      this.setState(1);
    });
  }
  
  public setState(state: number) {
    this.state.emit(state);
  }
  
  closeSettingsLinkDialog = () => {
    this.showSettingsLinkDialog = false;
    if (this.onDeleteLink) {
      this.showSettingsLinkDialog = false;
    } else {
      this.setState(1);
    }
    setTimeout(() => {
      this.showSettingsLinkDialogChange.next(false);
      this.close.emit(false);
    });
  }
  
  closeAfterSettingsSave = () => {
    this.showSettingsLinkDialog = false;
    setTimeout(() => {
      this.showSettingsLinkDialogChange.next(false);
    });
  }
  
  onDeleteLink = () => {
    this.closeSettingsLinkDialog();
    setTimeout(() => {
      this.setState(3);
    })
    this.close.emit(false);
  }
  
  redirectToUpgrade = () => {
    this.chargebeeService.openPortal();
  }
  
  linkFeatures = `
  <ul class="link-features">
    <li>Expiry dates</li>
    <li>File requests with upload-enabled links</li>
    <li>Set download limits (self-destruct)</li>
    <li>Preview only mode for documents and images</li>
    <li>Email notifications on link activity</li>
    <li>Download count stats</li>
  </ul>
`;
  
}
